import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import InputLabel from 'components/others/InputLabel';
import type { UserTabProps } from '../../types';

const UserTabGeneral = ({ user, dispatchUser }: UserTabProps) => {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const refInputAvatar = useRef<HTMLInputElement | null>(null);
    const [src, setSrc] = useState(user?.userPicture || '/imgs/user.png');
    const [objectPreviewUrl, setObjectPreviewUrl] = useState<string | null>(null);

    const handleChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget?.files?.[0];
        if (!file) return;

        const previewUrl = URL.createObjectURL(file);
        setSrc(previewUrl);
        setObjectPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return previewUrl;
        });
        /*
        dispatchUser({
            type: 'SET_GENERAL_FIELD',
            field: 'user_picture',
            value: previewUrl,
        });*/
        dispatchUser({
            type: 'SET_GENERAL_FIELD',
            field: '__avatar_file',
            value: file,
        });
    };

    const handleClickImg = () => {
        if (!refInputAvatar.current) return;
        refInputAvatar.current.value = '';
        refInputAvatar.current.click();
    };

    useEffect(() => {
        if (objectPreviewUrl) return;
        setSrc(user?.userPicture || '/imgs/user.png');
    }, [user?.userPicture, objectPreviewUrl]);

    useEffect(() => {
        return () => {
            if (objectPreviewUrl) URL.revokeObjectURL(objectPreviewUrl);
        };
    }, [objectPreviewUrl]);

    return (
        <>
            <div className="d-sm-flex">
                <input
                    ref={refInputAvatar}
                    type="file"
                    accept=".png,.jpeg,.jpg,.webp,.gif,.svg"
                    className="d-none"
                    onChange={handleChangeAvatar}
                />
                <div className="text-center pb-4 pe-4">
                    <img
                        className="wid-100 shadow rounded cursor-pointer"
                        src={src}
                        alt=""
                        onClick={handleClickImg}
                        onError={() => {
                            setSrc('/imgs/user.png');
                        }}
                    />
                </div>
                <div className="w-100">
                    <InputLabel title={t('Users.fieldUserName')} rightElement={null}>
                        <input
                            className="form-control"
                            type="text"
                            maxLength={250}
                            value={user?.userName || ''}
                            required
                            onChange={(e) => {
                                dispatchUser({
                                    type: 'SET_GENERAL_FIELD',
                                    field: 'userName',
                                    value: e.currentTarget.value,
                                });
                            }}
                        />
                    </InputLabel>

                    <InputLabel title={t('Users.fieldUserEmail')} rightElement={null}>
                        <input
                            className="form-control"
                            type="email"
                            value={user?.userEmail || ''}
                            required
                            disabled={user.ID !== 0}
                            onChange={(e) => {
                                if (user.ID === 0)
                                    dispatchUser({
                                        type: 'SET_GENERAL_FIELD',
                                        field: 'userEmail',
                                        value: user.ID === 0 ? e.currentTarget.value : user.userEmail,
                                    });
                            }}
                        />
                    </InputLabel>

                    {auth.user.ID !== user.ID && (
                        <>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="user_admin"
                                    checked={!!user?.admin}
                                    onChange={(e) => {
                                        dispatchUser({
                                            type: 'SET_GENERAL_FIELD',
                                            field: 'admin',
                                            value: e.currentTarget.checked ? 1 : 0,
                                        });
                                    }}
                                />
                                <InputLabel title={t('Users.fieldUserAdmin')} htmlFor="user_admin" rightElement={null} children={null} />
                            </div>

                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="user_status"
                                    checked={!!user?.userStatus}
                                    onChange={(e) => {
                                        dispatchUser({
                                            type: 'SET_GENERAL_FIELD',
                                            field: 'userStatus',
                                            value: e.currentTarget.checked ? 1 : 0,
                                        });
                                    }}
                                />
                                <InputLabel title={t('Users.fieldUserActive')} htmlFor="user_status" rightElement={null} children={null} />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserTabGeneral;
